import type { PurchaseOrder, Vendor } from '@ztechapps/common.contract'
import React, { type Dispatch, type SetStateAction, useContext as useContextCore, useState } from 'react'
import type { UseErrorResult } from '@ztechapps/common.hook'
import { useError } from '@ztechapps/common.hook'
import { useBillOfMaterialsContext } from '../Context'
import type { BomSummary } from '@ztechapps/product.common'

export type PurchaseButtonProviderProps = {
  children: React.ReactNode
  onAfterAction?: () => void
} & BomSummary
export type ActionType = 'add-new' | 'existing'
export type PurchaseButtonContext = {
  actionType: ActionType
  setActionType: Dispatch<SetStateAction<ActionType>>
  vendor: Vendor
  setVendor: Dispatch<SetStateAction<Vendor>>
  purchaseOrder: PurchaseOrder
  setPurchaseOrder: Dispatch<SetStateAction<PurchaseOrder>>
  truncatedProductName: string
  setShowConfirm: Dispatch<SetStateAction<boolean>>
  showConfirm: boolean
  useErrorResult: UseErrorResult
  purchaseOrderResult: PurchaseOrder
  setPurchaseOrderResult: Dispatch<SetStateAction<PurchaseOrder>>
  onAfterAction?: () => void
} & Omit<PurchaseButtonProviderProps, 'children'>

const context = React.createContext<PurchaseButtonContext>({} as PurchaseButtonContext)
const CoreProvider = context.Provider

export const PurchaseButtonProvider = ({ children, product, onAfterAction, ...props }: PurchaseButtonProviderProps) => {
  const [actionType, setActionType] = useState<ActionType>('add-new')
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder>(null)
  const [vendor, setVendor] = React.useState<Vendor>(null)
  const { truncatedProductName } = useBillOfMaterialsContext()
  const [showConfirm, setShowConfirm] = useState(false)
  const useErrorResult = useError()
  const [purchaseOrderResult, setPurchaseOrderResult] = useState<PurchaseOrder>(null)
  return (
    <CoreProvider
      value={{
        ...props,
        actionType,
        setActionType,
        vendor,
        setVendor,
        purchaseOrder,
        setPurchaseOrder,
        product,
        truncatedProductName,
        setShowConfirm,
        showConfirm,
        useErrorResult,
        purchaseOrderResult,
        setPurchaseOrderResult,
        onAfterAction,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const usePurchaseButtonContext = () => useContextCore(context)
